import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Eye, Mail, Phone, MapPin, User, Shield, CreditCard, Globe, Calendar, DollarSign, TrendingUp, Star, Award, CheckCircle, XCircle, Clock, Copy, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";

const ViewPayoutRequestModal = ({ request, onClose }) => {

  if (!request) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "paid":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "paid":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };



  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex gap-3 items-center">
            <div className="p-2 bg-[#FF6C6C] rounded-lg">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Payout Request Details</h2>
              <p className="text-sm text-gray-600">Review and process payout request</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column - Request Info */}
            <div className="space-y-6 lg:col-span-2">
              {/* Request Header */}
              <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9B9B]/10 rounded-xl border border-[#FF6C6C]/20">
                <div className="flex-shrink-0">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] flex items-center justify-center">
                    <span className="text-xl font-bold text-white">
                      {request.promoterName.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">{request.promoterName}</h3>
                  <div className="flex gap-4 items-center mb-3 text-sm text-gray-600">
                    <div className="flex gap-1 items-center">
                      <Mail className="w-4 h-4" />
                      {request.promoterEmail}
                    </div>
                    <div className="flex gap-1 items-center">
                      <Phone className="w-4 h-4" />
                      {request.promoterPhone}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="flex gap-2 items-center">
                      {getStatusIcon(request.status)}
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </div>
                    <span className="text-lg font-bold text-[#FF6C6C]">GH₵{request.amount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Product Information */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <Award className="w-5 h-5" />
                  Product Information
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm text-gray-600">Product Name</p>
                    <p className="font-medium text-gray-900">{request.productName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Product ID</p>
                    <p className="font-medium text-gray-900">{request.productId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Original Price</p>
                    <p className="font-medium text-gray-900">GH₵{request.originalPrice}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Deal Price</p>
                    <p className="font-medium text-[#FF6C6C]">GH₵{request.discountedPrice}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Commission Rate</p>
                    <p className="font-medium text-gray-900">{request.commissionRate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payout Amount</p>
                    <p className="font-bold text-[#FF6C6C] text-lg">GH₵{request.amount}</p>
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <TrendingUp className="w-5 h-5" />
                  Performance Metrics
                </h4>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <p className="text-sm text-gray-600">Clicks</p>
                    <p className="text-lg font-bold text-gray-900">{request.clicks}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Conversions</p>
                    <p className="text-lg font-bold text-gray-900">{request.conversions}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Conversion Rate</p>
                    <p className="text-lg font-bold text-gray-900">{request.conversionRate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tracking Code</p>
                    <div className="flex gap-2 items-center">
                      <p className="font-mono text-sm text-gray-900">{request.trackingCode}</p>
                      <button
                        onClick={() => copyToClipboard(request.trackingCode)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Affiliate Link */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <ExternalLink className="w-5 h-5" />
                  Affiliate Link
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={request.affiliateLink}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-gray-50 rounded-lg border border-gray-300"
                  />
                  <button
                    onClick={() => copyToClipboard(request.affiliateLink)}
                    className="px-3 py-2 text-sm text-[#FF6C6C] border border-[#FF6C6C] rounded-lg hover:bg-[#FF6C6C] hover:text-white transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <User className="w-5 h-5" />
                  Request Notes
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Promoter Notes</p>
                    <p className="text-gray-900">{request.notes || "No notes provided"}</p>
                  </div>
                  {request.adminNotes && (
                    <div>
                      <p className="text-sm text-gray-600">Admin Notes</p>
                      <p className="text-gray-900">{request.adminNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Actions & Info */}
            <div className="space-y-6">
              {/* Request Timeline */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <Calendar className="w-5 h-5" />
                  Request Timeline
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Request Date</p>
                    <p className="font-medium text-gray-900">{new Date(request.requestDate).toLocaleDateString()}</p>
                  </div>
                  {request.processedDate && (
                    <div>
                      <p className="text-sm text-gray-600">Processed Date</p>
                      <p className="font-medium text-gray-900">{new Date(request.processedDate).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payout Information */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <CreditCard className="w-5 h-5" />
                  Payout Information
                </h4>
                {request.payoutInfo.network ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Network</p>
                      <p className="font-medium text-gray-900 capitalize">{request.payoutInfo.network}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone Number</p>
                      <p className="font-medium text-gray-900">{request.payoutInfo.phoneNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Account Name</p>
                      <p className="font-medium text-gray-900">{request.payoutInfo.accountName}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No payout information configured</p>
                )}
              </div>

                             {/* Admin Actions */}
               {request.status === "pending" && (
                 <div className="p-6 bg-white rounded-xl border border-gray-200">
                   <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                     <Shield className="w-5 h-5" />
                     Admin Actions
                   </h4>
                   <div className="space-y-4">
                     <div className="text-sm text-gray-600">
                       Use the "Mark as Paid" button in the main table to process this payout request.
                     </div>
                   </div>
                 </div>
               )}

              {/* Status Information */}
              {request.status !== "pending" && (
                <div className="p-6 bg-white rounded-xl border border-gray-200">
                  <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                    <Shield className="w-5 h-5" />
                    Status Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex gap-2 items-center">
                      {getStatusIcon(request.status)}
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </div>
                    {request.adminNotes && (
                      <div>
                        <p className="text-sm text-gray-600">Admin Notes</p>
                        <p className="text-gray-900">{request.adminNotes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ViewPayoutRequestModal;
